import React, { useMemo } from 'react';
import { Card, CardContent, Typography, Box, Skeleton } from '@mui/material';
import { ResponsiveContainer, AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip } from 'recharts';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';

// --- Custom Components ---

// Tooltip and Y-Axis Tick components remain unchanged.
const CustomTooltip = ({ active, payload }: any) => {
  const displayLabel = payload?.[0]?.payload?.tooltipLabel || (payload && payload.length ? payload[0].payload.axisLabel : '');

  if (active && payload && payload.length) {
    return (
      <Card elevation={3} sx={{ borderRadius: '8px', padding: '12px', fontFamily: 'Inter' }}>
        <Typography sx={{ fontSize: '12px', color: '#666', marginBottom: '12px', textAlign: 'center', fontFamily: 'Inter' }}>
            {displayLabel}
        </Typography>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 4 }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <FiberManualRecordIcon sx={{ color: '#0064FF', fontSize: '10px', mr: 0.75 }} />
                <Typography sx={{ fontSize: '12px', fontWeight: 400, color: '#666666', fontFamily: 'Inter' }}>Traces</Typography>
            </Box>
            <Typography sx={{ fontSize: '12px', fontWeight: 500, color: '#333333', fontFamily: 'Inter' }}>
                {payload[0].value}
            </Typography>
        </Box>
      </Card>
    );
  }
  return null;
};

// **THIS IS THE FIX**
const CustomXAxisTick = ({ x, y, payload }: any) => {
  // FIX: Add a guard clause to ensure payload and its nested property exist before destructuring.
  // This prevents the runtime error.
  if (!payload || !payload.payload) {
    return null;
  }
  
  const { isTick, axisLabel } = payload.payload;

  if (!isTick) {
    return null;
  }
  
  return (
    <g transform={`translate(${x},${y})`}>
      <text x={0} y={0} dy={16} textAnchor="middle" fill="#666" style={{ fontSize: '12px', fontFamily: 'Inter', fontWeight: 400 }}>
        {axisLabel}
      </text>
    </g>
  );
};

const CustomYAxisTick = ({ x, y, payload }: any) => {
    if (!payload) return null;
    return (
      <g transform={`translate(${x},${y})`}>
        <text x={0} y={0} dy={4} textAnchor="end" fill="#666" style={{ fontSize: '12px', fontFamily: 'Inter', fontWeight: 400 }}>
          {payload.value}
        </text>
      </g>
    );
};

// --- Interfaces and Main Component ---

interface TraceDataPointBE {
  count: number;
  date: string;
}

interface TracesByTimeProps {
  traceData?: TraceDataPointBE[];
  isLoading?: boolean;
}

const TracesByTime: React.FC<TracesByTimeProps> = ({ traceData, isLoading }) => {

    const chartData = useMemo(() => {
        if (!traceData || traceData.length === 0) return [];
        
        const sortedData = [...traceData].sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
        const totalPoints = sortedData.length;
        const tickInterval = 7; 

        return sortedData.map((d, index) => {
            const dateObj = new Date(d.date);
            const adjustedDate = new Date(dateObj.valueOf() + dateObj.getTimezoneOffset() * 60 * 1000);

            const isTick = 
                index === 0 || 
                index === totalPoints - 1 || 
                index % tickInterval === 0;

            return {
                axisLabel: adjustedDate.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: '2-digit' }),
                tooltipLabel: adjustedDate.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' }),
                traces: d.count,
                isTick: isTick,
            };
        });
    }, [traceData]);

    const { yAxisTicks, yAxisDomain } = useMemo(() => {
        if (!chartData || chartData.length === 0) {
            return { yAxisTicks: [0, 10, 20, 30, 40], yAxisDomain: [0, 40] };
        }
        const maxVal = Math.max(...chartData.map(d => d.traces), 0);
        if (maxVal === 0) return { yAxisTicks: [0, 1, 2, 3, 4], yAxisDomain: [0, 4] };

        const niceCeiling = Math.ceil(maxVal / 4) * 4;
        const ticks = [0, niceCeiling * 0.25, niceCeiling * 0.5, niceCeiling * 0.75, niceCeiling];
        return { yAxisTicks: ticks, yAxisDomain: [0, niceCeiling] };
    }, [chartData]);


    return (
        <Card elevation={0} sx={{ borderRadius: '16px', height: '100%', fontFamily: 'Inter' }}>
            <CardContent sx={{ p: { xs: 2, md: 3 }, display: 'flex', flexDirection: 'column', height: '100%' }}>
                {isLoading ? (
                    <Skeleton variant="text" width="150px" height={28} sx={{ mb: 3 }} />
                ) : (
                    <Typography variant="h6" component="h3" sx={{ fontWeight: '600', fontSize: '16px', fontFamily: 'Inter', mb: 3 }}>
                        Traces By Time
                    </Typography>
                )}

                <Box sx={{ flexGrow: 1, width: '100%', height: '280px' }}>
                    {isLoading ? (
                        <Skeleton variant="rectangular" width="100%" height="100%" sx={{ borderRadius: '8px' }} />
                    ) : (
                        <ResponsiveContainer width="100%" height="100%">
                            <AreaChart
                                data={chartData}
                                margin={{ top: 10, right: 30, left: -25, bottom: 15 }}
                            >
                                <defs>
                                    <linearGradient id="colorTraces" x1="0" y1="0" x2="0" y2="1">
                                        <stop offset="5%" stopColor="#0064FF" stopOpacity={0.3}/>
                                        <stop offset="95%" stopColor="#0064FF" stopOpacity={0}/>
                                    </linearGradient>
                                </defs>
                                <CartesianGrid strokeDasharray="6 6" stroke="#E4EAF0" horizontal={true} vertical={false} />
                                <XAxis 
                                    dataKey="axisLabel" 
                                    axisLine={false} 
                                    tickLine={false} 
                                    interval={0}
                                    tick={<CustomXAxisTick />}
                                />
                                <YAxis 
                                    ticks={yAxisTicks}
                                    domain={yAxisDomain}
                                    axisLine={false} 
                                    tickLine={false}
                                    tick={<CustomYAxisTick />}
                                />
                                <Tooltip content={<CustomTooltip />} />
                                <Area type="monotone" dataKey="traces" stroke="#0064FF" strokeWidth={2} fillOpacity={1} fill="url(#colorTraces)" dot={{r: 4, fill: '#0064FF', stroke: 'white', strokeWidth: 2}} activeDot={{r: 6}} />
                            </AreaChart>
                        </ResponsiveContainer>
                    )}
                </Box>
            </CardContent>
        </Card>
    );
};

export default TracesByTime;